import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  Building2, Users, Truck, DollarSign, Activity, MessageSquare,
  Plus, ArrowRight, TrendingUp, Sparkles, ArrowUpRight,
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = ['#002FA7', '#34C759', '#FF9500', '#94A3B8'];
const PLAN_COLORS = {
  starter: '#94A3B8',
  growth: '#002FA7',
  enterprise: '#FF9500',
  none: '#E2E8F0',
};
const LEAD_COLORS = {
  new: '#FF9500',
  contacted: '#002FA7',
  qualified: '#7C3AED',
  won: '#34C759',
  lost: '#94A3B8',
};

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Animated count-up
const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = 0;
    const to = Number(target) || 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const KpiCard = ({ testid, icon: Icon, label, value, isMoney = false, accent = '#002FA7', sub }) => {
  const animated = useCountUp(value);
  const display = isMoney ? fmtMoney(Math.round(animated)) : Math.round(animated).toLocaleString();
  return (
    <div data-testid={testid} className="group relative bg-white border border-slate-200 rounded-xl p-6 overflow-hidden hover:border-slate-300 transition-colors">
      <div
        className="absolute -right-8 -top-8 w-28 h-28 rounded-full opacity-[0.08] group-hover:opacity-[0.14] transition-opacity"
        style={{ background: accent }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}1A` }}>
            <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={2.25} />
          </div>
          {sub != null && (
            <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <TrendingUp className="w-3 h-3" /> {sub}
            </div>
          )}
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
        <p className="text-3xl font-bold text-slate-900 mt-0.5 tabular-nums" style={{ fontFamily: 'Work Sans, sans-serif' }}>{display}</p>
      </div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, action, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-6">
    <div className="flex items-start justify-between mb-5">
      <div>
        <h2 className="text-base font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/superadmin/overview`, { withCredentials: true });
        if (mounted) setData(data);
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <DashboardLayout><div className="p-8 text-slate-600">Loading platform overview…</div></DashboardLayout>;
  }
  if (error) return <DashboardLayout><div className="p-8 text-red-600" data-testid="superadmin-error">{error}</div></DashboardLayout>;
  if (!data) return null;

  const { kpis, signups_by_month, role_distribution, subscription_distribution, top_companies, lead_status_breakdown, recent_leads } = data;

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto" data-testid="superadmin-dashboard">

        {/* Header */}
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-[11px] font-semibold mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Sparkles className="w-3 h-3" /> Platform overview
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1.5" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Super Admin Console
            </h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Real-time signals across every tenant on CivilX TMS.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="goto-leads"
              onClick={() => navigate('/admin/leads')}
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#0034BD] text-white px-4 py-2.5 rounded-md text-sm font-semibold transition-all"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <MessageSquare className="w-4 h-4" />
              View leads
              {kpis.new_leads > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-[#FF9500] rounded text-[10px] tabular-nums">{kpis.new_leads}</span>
              )}
            </button>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <KpiCard testid="kpi-companies" icon={Building2} label="Companies" value={kpis.companies} accent="#002FA7" />
          <KpiCard testid="kpi-users" icon={Users} label="Users" value={kpis.users} accent="#34C759" />
          <KpiCard testid="kpi-drivers" icon={Truck} label="Drivers" value={kpis.drivers} accent="#7C3AED" />
          <KpiCard testid="kpi-active-loads" icon={Activity} label="Active loads" value={kpis.active_loads} accent="#FF9500" />
          <KpiCard testid="kpi-mrr" icon={DollarSign} label="MRR" value={kpis.mrr} isMoney accent="#002FA7" />
          <KpiCard testid="kpi-leads" icon={MessageSquare} label="Leads" value={kpis.leads} accent="#34C759" sub={kpis.new_leads > 0 ? `${kpis.new_leads} new` : null} />
        </div>

        {/* Charts row 1: signups + role distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          <ChartCard
            testid="chart-signups"
            title="Company sign-ups"
            subtitle="Last 6 months"
            action={<span className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{kpis.companies} total</span>}
          >
            <div className="lg:col-span-2 h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <BarChart data={signups_by_month} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-signups" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#002FA7" stopOpacity={1} />
                      <stop offset="100%" stopColor="#3D7DFF" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(0,47,167,0.05)' }} />
                  <Bar
                    dataKey="companies"
                    fill="url(#grad-signups)"
                    radius={[8, 8, 0, 0]}
                    isAnimationActive
                    animationDuration={1100}
                    animationEasing="ease-out"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard testid="chart-roles" title="User roles" subtitle="Distribution across the platform">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <PieChart>
                  <Pie
                    data={role_distribution}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={3}
                    isAnimationActive
                    animationDuration={1100}
                    animationBegin={150}
                  >
                    {role_distribution.map((_, idx) => (
                      <Cell key={idx} fill={ROLE_COLORS[idx % ROLE_COLORS.length]} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* Charts row 2: subscriptions + leads breakdown + top companies */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          <ChartCard testid="chart-subscriptions" title="Subscriptions" subtitle={`${kpis.active_subscriptions} active · ${fmtMoney(kpis.mrr)} MRR`}>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <PieChart>
                  <Pie
                    data={subscription_distribution}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                    isAnimationActive
                    animationDuration={1100}
                    animationBegin={300}
                  >
                    {subscription_distribution.map((entry, idx) => (
                      <Cell key={idx} fill={PLAN_COLORS[entry.plan_id] || '#94A3B8'} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard testid="chart-lead-funnel" title="Lead funnel" subtitle={`${kpis.leads} leads · ${kpis.new_leads} new`}>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <BarChart data={lead_status_breakdown} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(0,47,167,0.05)' }} />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]} isAnimationActive animationDuration={1100} animationBegin={200}>
                    {lead_status_breakdown.map((entry, idx) => (
                      <Cell key={idx} fill={LEAD_COLORS[entry.status] || '#94A3B8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard testid="chart-top-companies" title="Top companies" subtitle="By driver count">
            {top_companies.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No data yet.</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                  <BarChart data={top_companies} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grad-top" x1="0" x2="1" y1="0" y2="0">
                        <stop offset="0%" stopColor="#3D7DFF" stopOpacity={0.7} />
                        <stop offset="100%" stopColor="#002FA7" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={130} interval={0} />
                    <Tooltip contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(0,47,167,0.05)' }} />
                    <Bar dataKey="drivers" fill="url(#grad-top)" radius={[0, 8, 8, 0]} isAnimationActive animationDuration={1100} animationBegin={400} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        </div>

        {/* Recent leads */}
        <ChartCard
          testid="recent-leads-card"
          title="Recent demo requests"
          subtitle="Last 5 inbound leads"
          action={
            <button
              data-testid="recent-leads-viewall"
              onClick={() => navigate('/admin/leads')}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:text-[#0034BD]"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              View all <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          }
        >
          {recent_leads.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No leads yet. Share your landing page to start collecting demo requests.</p>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-200">
                    {['Lead', 'Company', 'Email', 'Fleet', 'Status'].map((h) => (
                      <th key={h} className="text-left py-2.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recent_leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100">
                      <td className="py-3 text-sm font-medium text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{lead.name || '—'}</td>
                      <td className="py-3 text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{lead.company || '—'}</td>
                      <td className="py-3 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{lead.email}</td>
                      <td className="py-3 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{lead.fleet_size || '—'}</td>
                      <td className="py-3">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold"
                          style={{
                            color: LEAD_COLORS[lead.status] || '#475569',
                            backgroundColor: `${LEAD_COLORS[lead.status] || '#475569'}22`,
                            fontFamily: 'IBM Plex Sans, sans-serif',
                          }}
                        >
                          {(lead.status || 'new').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;
